// The camera's arithmetic (slice 06; design/08-map-on-the-table.md §4), pure
// and tested: a view is the board point at the screen's top-left and a
// scale. Zoom keeps the board point under the cursor where it is; panning
// is held to the board plus a quarter of overscroll on every side, so an
// edge can be pulled to the centre of the screen and nobody gets lost; fit
// brings the whole board on with a little air around it.
import { ZOOM_LIMITS, type BoardView } from '@dragons-table/vtt-shared';

export interface Extent { width: number; height: number }
export interface Viewport { width: number; height: number }

export const OVERSCROLL = 0.25;
const FIT_AIR = 0.95;

export const clampZoom = (zoom: number): number => Math.min(ZOOM_LIMITS.max, Math.max(ZOOM_LIMITS.min, zoom));

/**
 * Hold the visible window inside the board plus overscroll. When the window
 * is wider than the allowed range (zoomed far out), the board is centred.
 */
export function clampView(view: BoardView, extent: Extent, viewport: Viewport): BoardView {
  const zoom = clampZoom(view.zoom);
  const axis = (pos: number, size: number, screen: number): number => {
    const visible = screen / zoom;
    const lo = -size * OVERSCROLL;
    const hi = size * (1 + OVERSCROLL);
    if (visible >= hi - lo) return (size - visible) / 2;
    return Math.min(hi - visible, Math.max(lo, pos));
  };
  return { x: axis(view.x, extent.width, viewport.width), y: axis(view.y, extent.height, viewport.height), zoom };
}

/** Pan by a screen-pixel delta (the pointer moved by dx, dy; the board follows the hand). */
export function pan(view: BoardView, dx: number, dy: number, extent: Extent, viewport: Viewport): BoardView {
  return clampView({ x: view.x - dx / view.zoom, y: view.y - dy / view.zoom, zoom: view.zoom }, extent, viewport);
}

/** Zoom by a factor about a screen point: the board point under the cursor stays under the cursor. */
export function zoomAt(view: BoardView, factor: number, cursor: { x: number; y: number }, extent: Extent, viewport: Viewport): BoardView {
  const zoom = clampZoom(view.zoom * factor);
  const bx = view.x + cursor.x / view.zoom;
  const by = view.y + cursor.y / view.zoom;
  return clampView({ x: bx - cursor.x / zoom, y: by - cursor.y / zoom, zoom }, extent, viewport);
}

/** The whole board on screen at once, centred, with a little air. */
export function fitToScreen(extent: Extent, viewport: Viewport): BoardView {
  if (viewport.width <= 0 || viewport.height <= 0) return { x: 0, y: 0, zoom: 1 };
  const zoom = clampZoom(Math.min(viewport.width / extent.width, viewport.height / extent.height) * FIT_AIR);
  return { x: (extent.width - viewport.width / zoom) / 2, y: (extent.height - viewport.height / zoom) / 2, zoom };
}

/** The wheel's notch as a zoom factor: a notch is about ten per cent, in whichever unit the browser reports. */
export function wheelFactor(deltaY: number, deltaMode: number): number {
  const notches = deltaMode === 1 ? deltaY / 3 : deltaMode === 2 ? deltaY : deltaY / 100;
  return Math.pow(1.1, -Math.max(-3, Math.min(3, notches)));
}

export const sameView = (a: BoardView | null, b: BoardView | null): boolean =>
  a === b || (a !== null && b !== null && a.x === b.x && a.y === b.y && a.zoom === b.zoom);
